import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Star, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface InterestedUser {
  id: string;
  display_name: string;
  photo_url?: string;
  bio: string;
  verified: boolean;
  compatibility_score: number;
  move_in_date: string;
}

interface InterestedUsersModalProps {
  listingId: string;
  onClose: () => void;
}

export function InterestedUsersModal({ listingId, onClose }: InterestedUsersModalProps) {
  const [users, setUsers] = useState<InterestedUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInterestedUsers();
  }, [listingId]);

  const fetchInterestedUsers = async () => {
    try {
      const { data } = await supabase
        .from('listing_interests')
        .select(`
          user_id,
          profiles (
            id,
            display_name,
            photo_url,
            bio,
            verified,
            move_in_date
          )
        `)
        .eq('listing_id', listingId);

      if (data) {
        const interestedUsers = data.map(item => ({
          ...item.profiles,
          compatibility_score: Math.floor(Math.random() * 30) + 70 // Simulated score
        }));
        setUsers(interestedUsers);
      }
    } catch (error) {
      console.error('Error fetching interested users:', error);
    } finally {
      setLoading(false);
    }
  };

  const startChat = async (userId: string) => {
    // This would be implemented in a future update to handle messaging
    console.log('Starting chat with user:', userId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Interested Users</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              No interested users yet
            </div>
          ) : (
            <div className="space-y-6">
              {users.map(user => (
                <div key={user.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {user.photo_url ? (
                      <img 
                        src={user.photo_url} 
                        alt={user.display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary-100 flex items-center justify-center text-primary-600 text-xl font-semibold">
                        {user.display_name.charAt(0)}
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{user.display_name}</h3>
                      {user.verified && (
                        <Shield className="w-4 h-4 text-primary-400" />
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400" />
                        <span>{user.compatibility_score}% match</span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">{user.bio}</p>

                    <button
                      onClick={() => startChat(user.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>Message</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}